import { useCallback, useRef, useState } from 'react';

interface AudioProgressBarProps {
  /** Current progress percentage (0–100) */
  progress: number;
  /** Called when the user seeks to a new position */
  onSeek: (percent: number) => void;
  /** Whether audio is loaded and interactive */
  disabled?: boolean;
}

export const AudioProgressBar = ({ progress, onSeek, disabled = false }: AudioProgressBarProps) => {
  const [isHovering, setIsHovering] = useState(false);
  const [hoverPercent, setHoverPercent] = useState(0);
  const trackRef = useRef<HTMLDivElement | null>(null);

  const getPercentFromEvent = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
  }, []);

  const enabled = !disabled;

  return (
    <div
      ref={trackRef}
      role="slider"
      aria-label="Audio progress"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(progress)}
      tabIndex={enabled ? 0 : -1}
      className={`group/track relative min-w-8 flex-1 py-2 ${enabled ? 'cursor-pointer' : 'cursor-default opacity-50'}`}
      onMouseEnter={() => enabled && setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onMouseMove={(e) => {
        if (enabled) setHoverPercent(getPercentFromEvent(e));
      }}
      onClick={(e) => {
        if (!enabled) return;
        onSeek(getPercentFromEvent(e));
      }}
      onKeyDown={(e) => {
        if (!enabled) return;
        const step = e.shiftKey ? 10 : 2;
        let newProgress = progress;
        if (e.key === 'ArrowRight') newProgress = Math.min(100, progress + step);
        else if (e.key === 'ArrowLeft') newProgress = Math.max(0, progress - step);
        else return;
        e.preventDefault();
        onSeek(newProgress);
      }}
    >
      {/* Track background */}
      <div className="h-1 w-full rounded-full bg-muted">
        {/* Filled portion */}
        <div className="h-full rounded-full bg-primary" style={{ width: `${progress}%` }} />
      </div>

      {/* Hover ghost indicator */}
      {isHovering && enabled && (
        <div
          className="-translate-x-1/2 -translate-y-1/2 pointer-events-none absolute top-1/2 rounded-full bg-primary/30"
          style={{ left: `${hoverPercent}%`, width: 12, height: 12 }}
        />
      )}

      {/* Playback position thumb */}
      {enabled && (
        <div
          className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 rounded-full bg-primary shadow-sm transition-[width,height] duration-150"
          style={{
            left: `${progress}%`,
            width: isHovering ? 14 : 8,
            height: isHovering ? 14 : 8,
          }}
        />
      )}
    </div>
  );
};
